// Line Entry — the state behind one line form's commands.
//
// Runs a command end to end: the document chooser (when the command picks its
// documents), the criteria step (a General Process source with criteria the
// user may set), then either the picker (review / multi) or straight to
// `apply_lines` (picker "none"), and finally appends the engine-built lines to
// THIS form's buffer through the host's `onAppend`. No business logic runs
// here — every line value comes from the engine.

import { useCallback, useMemo, useRef, useState } from 'react'
import type { SqlModel } from '../types'
import { columnFormKey } from '../utils'
import {
  lineEntryApi, isUnsupportedAction, toBufferRow,
  type FetchLinesResponse, type LineCriteriaResponse, type LineEntryApi, type LineExecute,
  type LineRowResult,
} from './contract'
import { commandVisible, lineEntryOf, toolbarLayout, type NormalizedLineEntry } from './model'

export type LineCmd = NormalizedLineEntry['commands'][number]

/** Runtimes whose engine answered "unknown action" — the menu hides for the
 *  rest of the session so a mixed fleet degrades to manual entry. */
const unsupportedEngines = new Set<string>()

export interface LineEntryNotify {
  success: (m: string) => void
  error: (m: string) => void
  info?: (m: string) => void
}

export interface UseLineEntryOptions {
  sm: SqlModel
  formNo: string
  execute: LineExecute
  /** Identifies the transport for the unsupported-engine cache. */
  engineKey?: string
  headerValues: Record<string, unknown>
  parentPk?: Record<string, unknown>
  txnId?: string
  /** This form's buffer rows (deleted rows included — they are skipped). */
  rows: Array<Record<string, unknown>>
  onAppend: (rows: Array<Record<string, unknown>>) => void
  /** Remove buffer rows by index (for "replace the old document's lines"). */
  onRemove?: (indices: number[]) => void
  notify: LineEntryNotify
  readOnly?: boolean
}

export interface PickerState {
  cmd: LineCmd
  documents?: unknown[]
  first: FetchLinesResponse
  /** The calls of this run — bound to the criteria step's values, if any. */
  api: LineEntryApi
  /** The criteria step that led here (reopened by "Change criteria"). */
  criteria?: { step: LineCriteriaResponse; values: Record<string, unknown> }
}

/** How a command run ended, for a caller that started it from code (an
 *  'open' action button's follow-up step):
 *  - 'added'        lines were produced and appended (or none were offered);
 *  - 'interactive'  a chooser / criteria step / picker / replace question is
 *                   now open and the person finishes it;
 *  - 'failed'       the command could not run (reason already notified). */
export type LineRunOutcome = 'added' | 'interactive' | 'failed'

export interface CriteriaState {
  cmd: LineCmd
  documents?: unknown[]
  addAll: boolean
  step: LineCriteriaResponse
  values?: Record<string, unknown>
}

export interface ReplaceAsk {
  cmd: LineCmd
  rows: LineRowResult[]
  staleIndices: number[]
}

function info(n: LineEntryNotify, m: string): void {
  if (n.info) n.info(m)
  else n.success(m)
}

function normKey(k: unknown): string {
  return String(k ?? '').trim().replace(/\s+/g, '_').toLowerCase()
}

export function useLineEntry(o: UseLineEntryOptions) {
  const le = useMemo(() => lineEntryOf(o.sm), [o.sm])
  const engineKey = o.engineKey || 'default'
  const [unsupported, setUnsupported] = useState(() => unsupportedEngines.has(engineKey))
  const [busy, setBusy] = useState<string | null>(null)
  const [chooser, setChooser] = useState<LineCmd | null>(null)
  const [picker, setPicker] = useState<PickerState | null>(null)
  const [criteriaStep, setCriteriaStep] = useState<CriteriaState | null>(null)
  const [replaceAsk, setReplaceAsk] = useState<ReplaceAsk | null>(null)
  // The chooser was opened by a run that adds every line of the documents
  // picked (see `run`'s addAll).
  const chooserAddAll = useRef(false)

  // Latest options in a ref so async continuations read current values.
  const ref = useRef(o)
  ref.current = o

  const labelOf = useCallback((key: string) => {
    for (const w of o.sm.columns || []) {
      const c = w?.column
      if (c && columnFormKey(c) === key) return String(c.heading || c.name || key)
    }
    return key
  }, [o.sm])

  const api = useMemo(() => lineEntryApi(
    (action, payload) => ref.current.execute(action, payload),
    {
      get formNo() { return ref.current.formNo },
      get headerValues() { return ref.current.headerValues },
      get txnId() { return ref.current.txnId },
      get parentPk() { return ref.current.parentPk },
      get lines() { return ref.current.rows },
    },
  ), [])

  const visibleCommands = useMemo(
    () => (le?.commands || []).filter(c => commandVisible(c, o.headerValues || {})),
    [le, o.headerValues],
  )
  const layout = useMemo(
    () => (le ? toolbarLayout(le, c => visibleCommands.includes(c)) : null),
    [le, visibleCommands],
  )

  const fail = useCallback((what: string, err: unknown) => {
    if (isUnsupportedAction(err)) {
      unsupportedEngines.add(engineKey)
      setUnsupported(true)
      ref.current.notify.error('Adding lines this way is not available on this server yet.')
      return
    }
    const anyErr = err as { response?: { data?: { error?: { message?: string }; detail?: unknown } }; message?: string }
    const msg = anyErr?.response?.data?.error?.message
      || (typeof anyErr?.response?.data?.detail === 'string' ? anyErr.response.data.detail as string : '')
      || anyErr?.message || 'failed'
    ref.current.notify.error(`${what}: ${msg}`)
  }, [engineKey])

  /** The document column on the LINE that names where it came from (the
   *  line column sharing the source's documents.header_column). */
  const lineDocKey = useCallback((cmd: LineCmd): string | null => {
    const src = le?.sourcesByName[cmd.source]
    const hc = src?.documents?.header_column
    if (!hc) return null
    const k = normKey(hc)
    const hasCol = (o.sm.columns || []).some(w => w?.column && columnFormKey(w.column) === k)
    return hasCol ? k : null
  }, [le, o.sm])

  const finishAppend = useCallback((cmd: LineCmd, produced: LineRowResult[], removeIdx: number[]) => {
    const cur = ref.current
    const dupes = produced.filter(r => r._linked)
    const fresh = produced.filter(r => !r._linked)
    if (removeIdx.length && cur.onRemove) cur.onRemove(removeIdx)
    if (fresh.length) cur.onAppend(fresh.map(toBufferRow))
    const flagged = fresh.filter(r => r._row_error || Object.keys(r._errors || {}).length).length
    const parts = [`${fresh.length} line${fresh.length === 1 ? '' : 's'} added`]
    if (flagged) parts.push(`${flagged} need attention`)
    if (dupes.length) parts.push(`${dupes.length} already on this record`)
    if (fresh.length) cur.notify.success(parts.join(' · '))
    else info(cur.notify, parts.join(' · '))
    void cmd
  }, [])

  /** Append produced lines, honouring the source's `on_document_change`
   *  for lines of THIS source from a document no longer in play. */
  const deliver = useCallback((cmd: LineCmd, produced: LineRowResult[], documents?: unknown[]) => {
    const cur = ref.current
    const src = le?.sourcesByName[cmd.source]
    const docKey = lineDocKey(cmd)
    const mode = src?.on_document_change || 'ask'
    let stale: number[] = []
    if (docKey && documents && documents.length && cmd.documents !== 'choose') {
      const want = new Set(documents.map(d => String(d)))
      stale = cur.rows
        .map((r, i) => ({ r, i }))
        .filter(({ r }) => !r._deleted && (r._link as { source?: string } | undefined)?.source === cmd.source
          && r[docKey] != null && r[docKey] !== '' && !want.has(String(r[docKey])))
        .map(({ i }) => i)
    }
    if (stale.length && mode === 'ask' && cur.onRemove) {
      setReplaceAsk({ cmd, rows: produced, staleIndices: stale })
      return 'interactive' as const
    }
    finishAppend(cmd, produced, mode === 'replace' ? stale : [])
    return 'added' as const
  }, [le, lineDocKey, finishAppend])

  /** Lines for a command once its documents (and criteria) are known.
   *  `criteria` undefined = not asked yet: a General Process source asks the
   *  engine for its criteria step first. */
  const proceed = useCallback(async (cmd: LineCmd, documents?: unknown[], addAll = false,
    criteria?: { step: LineCriteriaResponse; values: Record<string, unknown> }): Promise<LineRunOutcome> => {
    setBusy(cmd.name)
    try {
      if (!criteria && le?.sourcesByName[cmd.source]?.from?.kind === 'process') {
        const step = await api.criteria(cmd.name, documents)
        if (step.ask) {
          setCriteriaStep({ cmd, documents, addAll, step })
          return 'interactive'
        }
      }
      const runApi = criteria && Object.keys(criteria.values).length
        ? api.withCriteria(criteria.values) : api
      if (cmd.picker === 'none' || addAll) {
        const res = await runApi.apply(cmd.name, 'all', documents)
        return deliver(cmd, res.rows || [], documents)
      }
      const first = await runApi.fetch(cmd.name, { documents })
      if (!first.rows.length && !first.total) {
        info(ref.current.notify, first.message || 'Nothing to add.')
        return 'added'
      }
      setPicker({ cmd, documents: documents ?? first.documents, first, api: runApi, criteria })
      return 'interactive'
    } catch (err) {
      fail(cmd.label, err)
      return 'failed'
    } finally {
      setBusy(null)
    }
  }, [api, le, deliver, fail])

  // Run one command. `addAll` (a caller running it from code, e.g. an 'open'
  // action button's follow-up) skips the picker and adds every candidate line
  // — the same as the chooser's "Add all". A command that must ask the person
  // for something (which documents, its criteria) still asks.
  const run = useCallback(async (name: string, opts?: { addAll?: boolean }): Promise<LineRunOutcome> => {
    if (ref.current.readOnly || unsupported) return 'failed'
    const cmd = le?.commands.find(c => c.name === name)
    if (!cmd) {
      if (opts) ref.current.notify.error(`This form has no line command "${name}".`)
      return 'failed'
    }
    const addAll = !!opts?.addAll
    const src = le?.sourcesByName[cmd.source]
    const hc = src?.documents?.header_column
    const headerDoc = hc ? ref.current.headerValues?.[normKey(hc)] : undefined
    const blank = headerDoc == null || headerDoc === ''
    if (cmd.documents === 'choose' || (cmd.documents === 'header_or_choose' && blank)) {
      setChooser(cmd)
      chooserAddAll.current = addAll
      return 'interactive'
    }
    if (cmd.documents === 'header' && blank) {
      ref.current.notify.error(`Choose ${hc} first.`)
      return 'failed'
    }
    return proceed(cmd, cmd.documents === 'none' ? undefined : [headerDoc], addAll)
  }, [le, unsupported, proceed])

  return {
    le,
    layout,
    visibleCommands,
    available: !!le && !unsupported && visibleCommands.length > 0,
    busy,
    run,
    api,
    labelOf,
    chooser,
    closeChooser: () => { setChooser(null); chooserAddAll.current = false },
    chooseDocuments: (docs: unknown[], mode: 'pick' | 'all' = 'pick') => {
      const cmd = chooser
      setChooser(null)
      const all = mode === 'all' || chooserAddAll.current
      chooserAddAll.current = false
      if (cmd && docs.length) void proceed(cmd, docs, all)
    },
    /** How many lines a document offers through a command (the banner). */
    countFor: async (command: string, document: unknown): Promise<number | null> => {
      try {
        const res = await api.fetch(command, { documents: [document], limit: 1 })
        return typeof res.total === 'number' ? res.total : null
      } catch (err) {
        if (isUnsupportedAction(err)) { unsupportedEngines.add(engineKey); setUnsupported(true) }
        return null
      }
    },
    criteriaStep,
    closeCriteria: () => setCriteriaStep(null),
    /** The user's values on the criteria step (unprotected criteria only). */
    submitCriteria: (values: Record<string, unknown>) => {
      const st = criteriaStep
      setCriteriaStep(null)
      if (st) void proceed(st.cmd, st.documents, st.addAll, { step: st.step, values })
    },
    picker,
    closePicker: () => setPicker(null),
    /** Back from the picker to its criteria step, with the values chosen. */
    changeCriteria: () => {
      const p = picker
      if (!p?.criteria) return
      setPicker(null)
      setCriteriaStep({ cmd: p.cmd, documents: p.documents, addAll: false,
        step: p.criteria.step, values: p.criteria.values })
    },
    /** The picker produced lines (apply_lines already ran). */
    pickerDone: (rows: LineRowResult[]) => {
      const p = picker
      setPicker(null)
      if (p) deliver(p.cmd, rows, p.documents)
    },
    replaceAsk,
    answerReplace: (replace: boolean | null) => {
      const ask = replaceAsk
      setReplaceAsk(null)
      if (!ask || replace === null) return
      finishAppend(ask.cmd, ask.rows, replace ? ask.staleIndices : [])
    },
    fail,
  }
}

export type LineEntryController = ReturnType<typeof useLineEntry>
